import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, Card, CardHeader, CardContent, Chip, Tooltip, Button} from 'views/components';
import {HelpOutlineOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';
import moment from 'moment';

const SituationCard = (props) => {
  const {
    clientRn,
    longSituationsCollapsed,
    handleCardCollapseToggle,
    newBadgeDurationHours,
    updateBadgeDurationHours,
    maxSummaryLength,
  } = props;

  const {
    situationRn,
    summary,
    sourceRn,
    source,
    weight,
    createdAt,
    updatedAt,
  } = props.situation;

  const linkableSources = [
    'COMMUNICATION_CONVERSATION_MESSAGES',
    'CLIENT_SUPPORT_EVENT_FEEDBACK',
  ];
  const sourceHref = linkableSources.includes(source) && sourceRn ?
    `/source/situation?${new URLSearchParams({clientRn, source, sourceRn}).toString()}` :
    null;
  const isClientMessageSource = source === 'CLIENT_MESSAGE' && Boolean(sourceRn);
  const isSourceClickable = Boolean(sourceHref || isClientMessageSource);

  const recentlyCreated = (createdAt) => createdAt && moment().valueOf()-moment(createdAt).valueOf() < 3600000*newBadgeDurationHours;
  const recentlyUpdated = (updatedAt) => updatedAt && moment().valueOf()-moment(updatedAt).valueOf() < 3600000*updateBadgeDurationHours;

  return (
    <Card
      key={situationRn}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        borderRadius: '16px',
        bgcolor: 'transparent',
      }}
    >
      <CardHeader
        title={
          <>
            <span>{situationRn}</span>
            <Typography
              variant='subtitle2'
              sx={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                fontWeight: 400,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#bcb8b1',
              }}
            >
              {weight}
            </Typography>
            {
              (recentlyCreated(createdAt) || recentlyUpdated(updatedAt)) &&
              <Stack direction='row' spacing={.5} sx={{position: 'absolute', left: -8, top: -8}}>
                {
                  recentlyCreated(createdAt) &&
                    <Tooltip title={translate('client.ClientDetail.Situation.Situation.Badge.NewTooltip', {hours: newBadgeDurationHours})}>
                      <Chip
                        size='small'
                        variant='filled'
                        color='error'
                        sx={{
                          borderRadius: '8px',
                        }}
                        label={<Translate expression='client.ClientDetail.Situation.Situation.Badge.New' />}
                      />
                    </Tooltip>
                }
                {
                  recentlyUpdated(updatedAt) &&
                    <Tooltip title={translate('client.ClientDetail.Situation.Situation.Badge.RecentlyUpdatedTooltip', {hours: updateBadgeDurationHours})}>
                      <Chip
                        size='small'
                        variant='filled'
                        color='info'
                        sx={{
                          borderRadius: '8px',
                        }}
                        label={<Translate expression='client.ClientDetail.Situation.Situation.Badge.RecentlyUpdated' />}
                      />
                    </Tooltip>
                }
              </Stack>
            }
          </>
        }
        titleTypographyProps={{fontSize: 13}}
        sx={{bgcolor: 'background.paper', borderRadius: '16px 16px 0 0'}}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '1rem',
          border: '1px solid #D0D1D6',
          borderRadius: '0 0 16px 16px',
        }}
      >
        {/* Main */}
        <Stack spacing={2}>
          <Stack direction='row' spacing={1}>
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '0.04em',
              }}
            >
              <Translate expression={`client.ClientDetail.Situation.Situation.TriggeringSource`} />
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: .5}}>
              <Typography
                variant='subtitle2'
                title={isSourceClickable ? sourceRn : undefined}
                component={sourceHref ? 'a' : 'span'}
                href={sourceHref || undefined}
                target={sourceHref ? '_blank' : undefined}
                rel={sourceHref ? 'noopener noreferrer' : undefined}
                onClick={isClientMessageSource ? () => props.onOpenClientMessage(sourceRn) : undefined}
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                  letterSpacing: '0.04em',
                  color: isSourceClickable ? 'primary.main' : 'inherit',
                  textDecoration: isSourceClickable ? 'underline' : 'none',
                  cursor: isSourceClickable ? 'pointer' : 'text',
                  border: 0,
                  bgcolor: 'transparent',
                  p: 0,
                }}
              >
                <Translate expression={`client.ClientDetail.Situation.Situation.SourceTypes.${source}`} />
              </Typography>
              {
                source === 'CLIENT_DETAILS' &&
                  <Tooltip title={translate('client.ClientDetail.Situation.Situation.Help.StatusSwitch')}>
                    <HelpOutlineOutlined
                      sx={{fontSize: '14px', lineHeight: '16px', color: '#6B6761'}}
                    />
                  </Tooltip>
              }
            </Box>
          </Stack>
          <Box>
            <Typography
              variant='subtitle2'
              component='span'
              sx={{
                fontWeight: 400,
                fontSize: '12px',
                letterSpacing: '0.04em',
                overflowWrap: 'anywhere',
              }}
            >
              {
                longSituationsCollapsed[situationRn] ? summary.slice(0, maxSummaryLength) : summary
              }
            </Typography>
            {
              longSituationsCollapsed[situationRn] !== undefined &&
                <Button
                  variant="text"
                  size="small"
                  sx={{
                    'p': 0,
                    'minWidth': 0,
                    'width': '1.25em',
                    'height': '1.25em',
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'justifyContent': 'center',
                    'verticalAlign': 'middle',
                    'lineHeight': 1,
                    'textTransform': 'none',
                    'borderRadius': '50%',

                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <Box
                    component="span"
                    onClick={() => handleCardCollapseToggle(situationRn)}
                    sx={{
                      display: 'block',
                      lineHeight: 1,
                    }}
                  >
                    ...
                  </Box>
                </Button>
            }
          </Box>
        </Stack>

        {/* Footer */}
        <Box>
          {
            updatedAt &&
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 400,
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#bcb8b1',
              }}
            >
              <Translate
                expression='client.ClientDetail.Situation.Situation.UpdatedAt' substitute={{time: moment(updatedAt).format('MMM D, h:mm A')}}
              />
            </Typography>
          }
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 400,
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#bcb8b1',
            }}
          >
            <Translate
              expression='client.ClientDetail.Situation.Situation.CreatedAt' substitute={{time: moment(createdAt).format('MMM D, h:mm A')}}
            />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

SituationCard.propTypes = {
  clientRn: PropTypes.string.isRequired,
  situation: PropTypes.object.isRequired,
  onOpenClientMessage: PropTypes.func.isRequired,
  longSituationsCollapsed: PropTypes.object.isRequired,
  handleCardCollapseToggle: PropTypes.func.isRequired,
  newBadgeDurationHours: PropTypes.number.isRequired,
  updateBadgeDurationHours: PropTypes.number.isRequired,
  maxSummaryLength: PropTypes.number.isRequired,
};

export default SituationCard;
